import Popover from "@govlaunch/popover";
import { EditorState, Modifier, SelectionState } from "draft-js";
import { List } from "immutable";
import PropTypes from "prop-types";
import React from "react";
import { Value } from "react-powerplug";
import SocialMediaInput, { SocialMedias as SupportedSocialMedias } from "../sidebar/SocialMediaInput";

export default function SocialMedias({ block, blockProps }) {
  const blockData = block.getData();

  const { editorState, readOnly, setReadOnly, onChange } = blockProps;

  const socialMedias = List.isList(blockData.get("socialMedias"))
    ? blockData.get("socialMedias").toJS()
    : blockData.get("socialMedias");

  if (!Array.isArray(socialMedias)) {
    return null;
  }

  if (socialMedias.length === 0) {
    return null;
  }

  const updateBlockData = modifiedData => {
    const content = editorState.getCurrentContent();

    const selection = new SelectionState({
      anchorKey: block.key,
      anchorOffset: 0,
      focusKey: block.key,
      focusOffset: block.getLength(),
    });

    const nextContentState = Modifier.mergeBlockData(content, selection, modifiedData);

    return EditorState.push(editorState, nextContentState, "change-block-data");
  };

  const removeCurrentBlock = () => {
    const selection = editorState.getSelection();
    const content = editorState.getCurrentContent();
    const keyAfter = content.getKeyAfter(block.key);
    const blockMap = content.getBlockMap().delete(block.key);

    const withoutAtomicBlock = content.merge({
      blockMap,
      selectionAfter: selection,
    });

    const newState = EditorState.push(editorState, withoutAtomicBlock, "remove-range");

    // if this is not the last block
    if (keyAfter) {
      const newSelection = new SelectionState({
        anchorKey: keyAfter,
        anchorOffset: 0,
        focusKey: keyAfter,
        focusOffset: block.getLength(),
      });

      return EditorState.forceSelection(newState, newSelection);
    }

    return newState;
  };

  return (
    <div
      css={{
        marginTop: 10,
      }}
    >
      <div
        css={{
          display: "grid",
          gridRowGap: 16,
          gridColumnGap: 8,
          gridTemplateColumns: "repeat(auto-fit, 32px)",
          position: "relative",
        }}
      >
        {socialMedias
          .map(({ socialMedia, url }) => {
            if (SupportedSocialMedias[socialMedia]) {
              const Icon = SupportedSocialMedias[socialMedia].icon;

              if (Icon) {
                return (
                  <a
                    key={socialMedia}
                    href={url}
                    rel="noopener noreferrer"
                    target="_blank"
                    title={SupportedSocialMedias[socialMedia].name}
                    css={{
                      display: "inline-flex",
                      userSelect: "none",
                      "& > svg": {
                        display: "block",
                      },
                    }}
                  >
                    <Icon {...SupportedSocialMedias[socialMedia].iconProps} />
                  </a>
                );
              }
            }

            return null;
          })
          .filter(Boolean)}

        <Value initial={false}>
          {({ value: isOpen, set: setOpen }) => {
            if (readOnly && !isOpen) {
              return null;
            }

            return (
              <Popover
                isOpen={isOpen}
                boxStyle={{
                  zIndex: 9999,
                  borderRadius: 4,
                  boxShadow: "0 10px 40px 0 rgba(34, 35, 40, 0.2)",
                }}
                onClickOutside={() => setOpen(false)}
                render={() => {
                  const defaultValues = socialMedias.reduce((values, currentValue) => {
                    return {
                      ...values,
                      [currentValue.socialMedia]: currentValue.url,
                    };
                  }, {});

                  return (
                    <SocialMediaInput
                      defaultValues={defaultValues}
                      onInsert={blockData => {
                        if (blockData.socialMedias.length === 0) {
                          onChange(removeCurrentBlock());
                        } else {
                          onChange(updateBlockData(blockData));
                          setOpen(false);
                        }

                        setTimeout(() => {
                          setReadOnly(false);
                        }, 1);
                      }}
                    />
                  );
                }}
              >
                {({ innerRef }) => {
                  return (
                    <button
                      onClick={() => {
                        setOpen(!isOpen);
                        setReadOnly(true);
                      }}
                      ref={innerRef}
                      type="button"
                      css={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: 0,
                        color: "#333333",
                        border: "none",
                        borderRadius: "100%",
                        width: 32,
                        height: 32,
                        outline: "none",
                        cursor: "pointer",
                        appearance: "button",
                        "&:hover": {
                          color: "rgba(0, 0, 0, 0.6)",
                          backgroundColor: "#f1f1f1",
                        },
                        WebkitAppearance: "button",
                        "& > svg": {
                          width: 18,
                          height: 18,
                        },
                      }}
                    >
                      <svg
                        width={12}
                        height={12}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                          fill: "none",
                        }}
                      >
                        <path d="M17 3a2.828 2.828 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                      </svg>
                    </button>
                  );
                }}
              </Popover>
            );
          }}
        </Value>
      </div>
    </div>
  );
}

SocialMedias.propTypes = {
  block: PropTypes.object.isRequired,
  blockProps: PropTypes.object.isRequired,
};
